<script setup>
/**
 * SzContactsEmpty — painel central quando nenhum contato selecionado.
 * 3 cards de ação: Importar / Exportar / Novo contato.
 */
import { ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

const emit = defineEmits(['createContact', 'importContacts']);
const { t } = useI18n();
const store = useStore();

const isExporting = ref(false);

async function onCreate() { emit('createContact'); }
async function onImport() { emit('importContacts'); }
async function onExport() {
  if (isExporting.value) return;
  isExporting.value = true;
  // SZ_CONTACTS_EXPORT_FIX_20260525: action correta é `contacts/export` (não
  // `exportContacts`) e recebe `{ payload, label }`. i18n key estava errada
  // (faltava .HEADER.ACTIONS no path) — usando texto literal pra robustez.
  try {
    await store.dispatch('contacts/export', { payload: [], label: '' });
    useAlert('Exportando. Você receberá o arquivo por e-mail quando estiver pronto.');
  } catch {
    useAlert('Falha ao exportar contatos. Tente novamente.');
  } finally {
    isExporting.value = false;
  }
}
</script>

<template>
  <div class="sz-cempty">
    <!-- Ícone central -->
    <div class="sz-cempty-icon-wrap">
      <div class="sz-cempty-icon-ring-outer">
        <div class="sz-cempty-icon-ring-inner">
          <div class="sz-cempty-icon-center">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#388E3C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <div class="sz-cempty-title">NENHUM CONTATO SELECIONADO</div>

    <div class="sz-cempty-cards">
      <!-- Importar -->
      <div class="sz-cempty-card" role="button" tabindex="0" @click="onImport" @keydown.enter.prevent="onImport">
        <div class="sz-cempty-card-icon sz-icon-green">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </div>
        <div class="sz-cempty-card-body">
          <div class="sz-cempty-card-title">Importar contatos em massa</div>
          <div class="sz-cempty-card-desc">Importe uma lista de contatos.</div>
        </div>
        <div class="sz-cempty-card-chevron">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
        </div>
      </div>

      <!-- Exportar -->
      <div class="sz-cempty-card" role="button" tabindex="0" @click="onExport" @keydown.enter.prevent="onExport">
        <div class="sz-cempty-card-icon sz-icon-green">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
        </div>
        <div class="sz-cempty-card-body">
          <div class="sz-cempty-card-title">Exportar contatos em massa</div>
          <div class="sz-cempty-card-desc">Exporte uma lista de contatos.</div>
        </div>
        <div class="sz-cempty-card-chevron">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
        </div>
      </div>

      <!-- Novo contato -->
      <div class="sz-cempty-card" role="button" tabindex="0" @click="onCreate" @keydown.enter.prevent="onCreate">
        <div class="sz-cempty-card-icon sz-icon-purple">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="8.5" cy="7" r="4"/>
            <path d="M20 8v6M23 11h-6"/>
          </svg>
        </div>
        <div class="sz-cempty-card-body">
          <div class="sz-cempty-card-title">Novo contato</div>
          <div class="sz-cempty-card-desc">Cadastre um novo contato no sistema.</div>
        </div>
        <div class="sz-cempty-card-chevron">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sz-cempty {
  flex: 1 1 0%;
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  box-sizing: border-box;
  font-family: 'Inter', system-ui, sans-serif;
}

/* Ícone com anéis */
.sz-cempty-icon-wrap { display: flex; align-items: center; justify-content: center; }
.sz-cempty-icon-ring-outer {
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: rgba(56, 142, 60, 0.04);
  display: flex; align-items: center; justify-content: center;
}
.sz-cempty-icon-ring-inner {
  width: 100px; height: 100px;
  border-radius: 50%;
  background: rgba(56, 142, 60, 0.06);
  display: flex; align-items: center; justify-content: center;
}
.sz-cempty-icon-center {
  width: 64px; height: 64px;
  border-radius: 50%;
  background: #EEF6F0;
  display: flex; align-items: center; justify-content: center;
}
.dark .sz-cempty-icon-center { background: rgba(56, 142, 60, 0.16); }

.sz-cempty-title {
  font-size: 13px;
  font-weight: 700;
  color: #262626;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-top: 4px;
}
.dark .sz-cempty-title { color: #FFFFFF; }

/* Cards de ação (~338x72) */
.sz-cempty-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 360px;
  margin-top: 12px;
}
.sz-cempty-card {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #FFFFFF;
  border: 1px solid #E5E5E5;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
}
.sz-cempty-card:hover {
  background: #FAFAFA;
  border-color: #D4D4D4;
}
.dark .sz-cempty-card {
  background: #1D1E21;
  border-color: #282A2E;
}
.dark .sz-cempty-card:hover {
  background: #242528;
  border-color: #3D3F43;
}

.sz-cempty-card-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.sz-cempty-card-icon.sz-icon-green {
  background: #EEF6F0;
  color: #388E3C;
}
.sz-cempty-card-icon.sz-icon-purple {
  background: #ECE9FC;
  color: #673BCB;
}
.dark .sz-cempty-card-icon.sz-icon-green {
  background: rgba(56, 142, 60, 0.16);
}
.dark .sz-cempty-card-icon.sz-icon-purple {
  background: rgba(103, 59, 203, 0.16);
}

.sz-cempty-card-body {
  flex: 1;
  min-width: 0;
}
.sz-cempty-card-title {
  font-size: 13px;
  font-weight: 600;
  color: #262626;
  line-height: 18px;
}
.sz-cempty-card-desc {
  font-size: 11px;
  font-weight: 400;
  color: #A1A1A1;
  line-height: 16px;
  margin-top: 2px;
}
.dark .sz-cempty-card-title { color: #FFFFFF; }
.dark .sz-cempty-card-desc { color: #9797A0; }

.sz-cempty-card-chevron {
  color: #A1A1A1;
  flex-shrink: 0;
}
.dark .sz-cempty-card-chevron { color: #6B7280; }
</style>
